import React from "react";
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import {
	faXTwitter,
	faGithub,
	faLinkedin,
	faFacebook,
	faReddit,
} from "@fortawesome/free-brands-svg-icons";

import { useUserInfo } from "../../hooks/useUserInfo";
import { useTheme } from "../../contexts/ThemeContext";
import { Theme } from "../../theme";

const Container = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 0.75rem;
	margin-top: 1.5rem;

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

const SocialCard = styled.div<{ theme: Theme }>`
	a {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		min-height: 4.25rem;
		padding: 0.75rem;
		background: ${props => props.theme.colors.background.secondary};
		border-radius: ${props => props.theme.density.cardRadius};
		text-decoration: none;
		transition: border-color 0.2s ease, transform 0.2s ease;
		box-shadow: ${props => props.theme.shadows.base};
		border: 1px solid ${props => props.theme.colors.neutral[200]};

		&:hover {
			transform: translateY(-2px);
			border-color: ${props => props.theme.colors.primary.main};
		}

		&:focus-visible {
			outline: 2px solid ${props => props.theme.colors.primary.main};
			outline-offset: 2px;
		}
	}
`;

const SocialIcon = styled.div<{ theme: Theme }>`
	font-size: 1rem;
	color: ${props => props.theme.colors.primary.main};
	width: 2.5rem;
	height: 2.5rem;
	flex: 0 0 2.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 0.75rem;
`;

const SocialText = styled.div<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	font-weight: 550;
	color: ${props => props.theme.colors.text.primary};
	flex: 1;
`;

const EmailCard = styled(SocialCard)`
	grid-column: 1 / -1;
`;

const Socials: React.FC = () => {
	const { theme } = useTheme();
	const userInfo = useUserInfo();

	return (
		<Container>
			<SocialCard theme={theme}>
				<a href={userInfo.socials.twitter} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faXTwitter} />
					</SocialIcon>
					<SocialText theme={theme}>Follow on X</SocialText>
				</a>
			</SocialCard>

			<SocialCard theme={theme}>
				<a href={userInfo.socials.github} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faGithub} />
					</SocialIcon>
					<SocialText theme={theme}>Follow on GitHub</SocialText>
				</a>
			</SocialCard>

			<SocialCard theme={theme}>
				<a href={userInfo.socials.linkedin} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faLinkedin} />
					</SocialIcon>
					<SocialText theme={theme}>Follow on LinkedIn</SocialText>
				</a>
			</SocialCard>

			<SocialCard theme={theme}>
				<a href={userInfo.socials.facebook} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faFacebook} />
					</SocialIcon>
					<SocialText theme={theme}>Follow on Facebook</SocialText>
				</a>
			</SocialCard>

			<SocialCard theme={theme}>
				<a href={userInfo.socials.reddit} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faReddit} />
					</SocialIcon>
					<SocialText theme={theme}>Follow on Reddit</SocialText>
				</a>
			</SocialCard>

			<EmailCard theme={theme}>
				<a href={`mailto:${userInfo.main.email}`} target="_blank" rel="noreferrer">
					<SocialIcon theme={theme}>
						<FontAwesomeIcon icon={faEnvelope} />
					</SocialIcon>
					<SocialText theme={theme}>{userInfo.main.email}</SocialText>
				</a>
			</EmailCard>
		</Container>
	);
};

export default Socials;
